import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Clock, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import { getCategoryLabel } from '../lib/categories';

export const RecipeCard = ({ recipe, index = 0 }) => {
  const { user, userProfile, toggleFavorite } = useAuth();
  const isFavorite = userProfile?.favorites?.includes(recipe.id);

  const handleFavoriteClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (user) {
      await toggleFavorite(recipe.id);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      whileHover={{ y: -4 }}
      className="group"
    >
      <Link to={`/rezept/${recipe.id}`} data-testid={`recipe-card-${recipe.id}`}>
        <article className="bg-white rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden">
          {/* Image Container */}
          <div className="relative aspect-[4/3] overflow-hidden">
            <img
              src={recipe.imageUrl || 'https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg'}
              alt={recipe.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
            
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            {/* Category Badge */}
            {recipe.category && (
              <span className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-navy text-xs font-semibold px-3 py-1 rounded-full">
                {getCategoryLabel(recipe.category)}
              </span>
            )}
            
            {/* Favorite Button */}
            {user && (
              <button
                onClick={handleFavoriteClick}
                className={cn(
                  "absolute top-3 right-3 p-2 rounded-full transition-all duration-300",
                  isFavorite 
                    ? "bg-terracotta text-white" 
                    : "bg-white/90 backdrop-blur-sm text-navy hover:bg-terracotta hover:text-white"
                )}
                data-testid={`favorite-btn-${recipe.id}`}
              >
                <Heart className={cn("w-4 h-4", isFavorite && "fill-current")} />
              </button>
            )}
          </div>

          {/* Content */}
          <div className="p-5">
            <h3 className="font-display text-lg font-semibold text-navy mb-2 line-clamp-2 group-hover:text-terracotta transition-colors">
              {recipe.title}
            </h3>
            
            {recipe.description && (
              <p className="text-olive text-sm mb-4 line-clamp-2">
                {recipe.description}
              </p>
            )}

            {/* Meta Info */}
            <div className="flex items-center gap-4 text-sm text-olive">
              {recipe.prepTime && (
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{recipe.prepTime} Min</span>
                </div>
              )}
              {recipe.servings && (
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  <span>{recipe.servings} Portionen</span>
                </div>
              )}
            </div>
          </div>
        </article>
      </Link>
    </motion.div>
  );
};
